import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import {
    getSubAgentDefinition,
    getInsightsForAgent,
    generateQueriesFromInsights,
    deleteGeneratedQuery,
    generateQueryForInsight
} from '../services/agentApi';
import { copyTextToClipboard } from '../utils/clipboard';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import {
    ArrowLeft,
    Loader,
    Code,
    Sparkles,
    TrendingUp,
    Trash2,
    Play,
    Copy,
    Plus,
    Activity,
    MessageSquare,
    X
} from 'lucide-react';

/**
 * SubAgentQueries - Page for viewing generated queries for a sub-agent.
 * Shows queries from database if they exist, or generates them using Claude Coder CLI.
 * Layout: Left sidebar for insight selection, right panel for query details.
 */
export const SubAgentQueries = () => {
    const { t } = useTranslation(['page-subagent-queries', 'common']);
    const navigate = useNavigate();
    const location = useLocation();
    const { agentId } = useParams();
    const { showToast } = useToast();

    const agentName = location.state?.agentName || t('defaultAgentName');
    const databaseIdFromState = location.state?.databaseId || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selectedInsightIds = useMemo(() => location.state?.selectedInsightIds || [], [location.state]);

    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [insightsWithQueries, setInsightsWithQueries] = useState([]);
    const [databaseId, setDatabaseId] = useState(databaseIdFromState || null);
    const [selectedInsightId, setSelectedInsightId] = useState(null);

    // Sync selectedInsightId to router history state so back navigation restores it
    useEffect(() => {
        if (selectedInsightId && location.state) {
            const currentSelected = location.state.selectedInsightIds?.[0];
            if (currentSelected !== selectedInsightId) {
                navigate(location.pathname, {
                    replace: true,
                    state: {
                        ...location.state,
                        selectedInsightIds: [selectedInsightId]
                    }
                });
            }
        }
    }, [selectedInsightId, location.state, location.pathname, navigate]);

    // NLP Query generation modal state
    const [showNLPModal, setShowNLPModal] = useState(false);
    const [nlpQuestion, setNlpQuestion] = useState('');
    const [generatingNLP, setGeneratingNLP] = useState(false);
    const [selectedInsightForNLP, setSelectedInsightForNLP] = useState(null);

    // Fetch agent definition and insights with their queries
    const fetchInsightsWithQueries = useCallback(async () => {
        setLoading(true);
        try {
            // Get agent definition to find the database ID
            const agentRes = await getSubAgentDefinition(agentId);
            if (agentRes.data?.status === 'success') {
                const agent = agentRes.data.data;
                setDatabaseId(agent.database_connection_id);

                // Get insights for this agent (insights include their generated_queries)
                const insightsRes = await getInsightsForAgent(agentId, agent.database_connection_id);
                if (insightsRes.data?.status === 'success') {
                    const insightsData = insightsRes.data.data?.insights || [];
                    setInsightsWithQueries(insightsData);
                    
                    if (insightsData.length > 0) {
                        setSelectedInsightId(prev => {
                            if (prev && insightsData.some(i => i.id.toString() === prev.toString())) {
                                return prev;
                            }
                            // Select the insight passed from navigation state, or fallback to the first one with queries
                            if (selectedInsightIds && selectedInsightIds.length > 0 && insightsData.some(i => i.id.toString() === selectedInsightIds[0].toString())) {
                                return selectedInsightIds[0];
                            }
                            const firstWithQueries = insightsData.find(i => i.generated_queries?.length > 0) || insightsData[0];
                            return firstWithQueries.id;
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Failed to fetch insights:', error);
            showToast(t('toastFailedLoadInsights'), 'error');
        } finally {
            setLoading(false);
        }
    }, [agentId, selectedInsightIds, showToast, t]);

    useEffect(() => {
        fetchInsightsWithQueries();
    }, [fetchInsightsWithQueries]);

    // Handle generating queries if they don't exist
    const handleGenerateQueries = async () => {
        if (!selectedInsightId) {
            showToast(t('toastNoInsightSelected'), 'error');
            return;
        }

        setGenerating(true);
        try {
            const response = await generateQueriesFromInsights([selectedInsightId]);
            if (response.data?.status === 'success') {
                showToast(t('toastQueriesGenerated'), 'success');
                // Refresh insights with new queries
                await fetchInsightsWithQueries();
            }
        } catch (error) {
            console.error('Failed to generate queries:', error);
            showToast(error.response?.data?.detail || t('toastFailedGenerateQueries'), 'error');
        } finally {
            setGenerating(false);
        }
    };

    // NLP Query generation handlers
    const openNLPModal = (insight) => {
        setSelectedInsightForNLP(insight);
        // Clear the question field for fresh input
        setNlpQuestion('');
        setShowNLPModal(true);
    };

    const handleNLPQuestionChange = (e) => {
        setNlpQuestion(e.target.value);
        const textarea = e.target;
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 300) + 'px';
        textarea.style.overflowY = textarea.scrollHeight > 300 ? 'auto' : 'hidden';
    };

    const closeNLPModal = () => {
        setShowNLPModal(false);
        setNlpQuestion('');
        setSelectedInsightForNLP(null);
    };

    const handleGenerateNLPQuery = async () => {
        if (!nlpQuestion.trim() || !databaseId || !selectedInsightForNLP) {
            showToast(t('toastEnterQuestion'), 'error');
            return;
        }

        setGeneratingNLP(true);
        try {
            // Use the new endpoint that generates query for existing insight
            const response = await generateQueryForInsight(
                selectedInsightForNLP.id,
                nlpQuestion
            );

            if (response.data?.status === 'success') {
                showToast(t('toastQueryGenerated'), 'success');
                closeNLPModal();
                // Refresh the query list to show the new query
                await fetchInsightsWithQueries();
            }
        } catch (error) {
            console.error('Failed to generate NLP query:', error);
            showToast(error.response?.data?.detail || t('toastFailedGenerateQuery'), 'error');
        } finally {
            setGeneratingNLP(false);
        }
    };

    // Get selected insight
    const selectedInsight = insightsWithQueries.find(i => i.id === selectedInsightId);

    // Check if we have any queries
    const totalQueries = insightsWithQueries.reduce(
        (sum, insight) => sum + (insight.generated_queries?.length || 0),
        0
    );
    const hasQueries = totalQueries > 0;

    // Execute query (navigate to execution page)
    const handleExecuteQuery = (query) => {
        navigate(`/agents/${agentId}/execute/${query.id}`, {
            state: {
                query,
                databaseId,
                agentName
            }
        });
    };

    // Compare query (navigate to compare page)
    const handleCompareQuery = async (query) => {
        setLoadingCompare(query.id);
        try {
            navigate(`/agents/${agentId}/compare/${query.id}`, {
                state: {
                    query,
                    databaseId,
                    agentName
                }
            });
        } finally {
            setTimeout(() => clearLoadingCompare(query.id), 100);
        }
    };

    // Loading state for compare button
    const [loadingCompareId, setLoadingCompareId] = useState(null);
    const setLoadingCompare = (id) => setLoadingCompareId(id);
    const clearLoadingCompare = (id) => {
        setLoadingCompareId(prev => prev === id ? null : prev);
    };

    // Delete confirmation modal state
    const [deleteConfirmModal, setDeleteConfirmModal] = useState({
        isOpen: false,
        queryId: null,
        queryName: null,
        title: t('deleteQueryTitle'),
        message: t('deleteQueryMessage', { name: '' }),
    });

    // Delete query handlers
    const openDeleteModal = (query) => {
        setDeleteConfirmModal({
            isOpen: true,
            queryId: query.id,
            queryName: query.name,
            title: t('deleteQueryTitle'),
            message: t('deleteQueryMessage', { name: query.name }),
        });
    };

    const closeDeleteModal = () => {
        setDeleteConfirmModal(prev => ({ ...prev, isOpen: false, queryId: null, queryName: null }));
    };

    const confirmDeleteQuery = async () => {
        if (!deleteConfirmModal.queryId) return;

        try {
            await deleteGeneratedQuery(deleteConfirmModal.queryId);
            showToast(t('toastQueryDeleted'), 'success');
            closeDeleteModal();
            await fetchInsightsWithQueries();
        } catch (error) {
            console.error('Failed to delete query:', error);
            showToast(error.response?.data?.detail || t('toastFailedDeleteQuery'), 'error');
        }
    };

    // Copy to clipboard
    const copyToClipboard = async (text) => {
        try {
            await copyTextToClipboard(text);
            showToast(t('toastQueryCopied'), 'success');
        } catch (error) {
            console.error('Failed to copy:', error);
            showToast(t('toastFailedCopyQuery'), 'error');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-white">
                <Loader className="w-12 h-12 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Top Page Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate(`/agents/insights/${agentId}`, { state: { agentName, databaseId: databaseIdFromState || databaseId } })}
                    className="w-10 h-10 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm flex-shrink-0"
                >
                    <ArrowLeft className="w-5 h-5 text-gray-500" />
                </button>
                <div>
                    <h1 className="text-xl font-bold text-gray-900 leading-tight">
                        {selectedInsight?.name || t('defaultInsightName')}
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {t('subAgentLabel', { name: agentName })}
                    </p>
                </div>
            </div>

            {/* Layout Grid */}
            <div className="flex gap-theme">
                {/* Left Sidebar - Insights */}
                <div className="w-[300px] flex-shrink-0 bg-white border border-gray-100 rounded-2xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.05)] overflow-hidden flex flex-col max-h-[80vh]">
                    <div className="pt-6 px-6 pb-4">
                        <h2 className="text-base font-bold text-gray-900">{t('insights')}</h2>
                    </div>

                    <div className="px-6 pb-6 overflow-y-auto space-y-3 flex-1 custom-scrollbar">
                        {insightsWithQueries.map((insight) => {
                            const queryCount = insight.generated_queries?.length || 0;
                            const isSelected = selectedInsightId === insight.id;
                            
                            return (
                                <button
                                    key={insight.id}
                                    onClick={() => setSelectedInsightId(insight.id)}
                                    className={`sidebar-list-button w-full text-left p-4 rounded-xl border-2 transition-all ${
                                        isSelected
                                            ? 'border-primary-200 bg-primary-50/40 shadow-sm'
                                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50 bg-white'
                                    }`}
                                >
                                    <h3 className={`font-semibold text-sm mb-2 leading-tight ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                                        {insight.name}
                                    </h3>
                                    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium ${
                                        queryCount > 0
                                            ? 'bg-green-100/50 text-green-700 border border-green-200/50'
                                            : 'bg-gray-100/80 text-gray-500 border border-gray-200/50'
                                    }`}>
                                        <Activity className={`w-3 h-3 ${queryCount > 0 ? 'text-green-500' : 'text-gray-400'}`} />
                                        {queryCount > 0 ? t('queryCountWithCount', { count: queryCount }) : t('noQuery')}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Right Content */}
                <div className="flex-1 min-w-0 flex flex-col max-w-5xl">
                    <div className="mb-6">
                        <div className="flex items-start justify-between">
                            <div className="pr-10">
                                <h2 className="text-lg font-bold text-gray-900 mb-1.5 leading-tight">
                                    {selectedInsight?.name || t('defaultInsightName')}
                                </h2>
                                <p className="text-sm text-gray-500 leading-relaxed mb-4 max-w-4xl">
                                    {selectedInsight?.description || t('defaultInsightDescription')}
                                </p>
                                <div className="flex items-center">
                                    <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-green-50 text-green-700 border border-green-200 flex items-center gap-1.5 shadow-sm">
                                        {t('queryGenerated')}
                                        <span className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center ml-1">
                                            <Activity className="w-2.5 h-2.5 text-green-600" />
                                        </span>
                                        {t('queryCountWithCount', { count: selectedInsight?.generated_queries?.length || 0 })}
                                    </span>
                                </div>
                            </div>
                            <div className="flex-shrink-0">
                                {/* Add Query button matching the image's blue button */}
                                <button
                                    onClick={() => openNLPModal(selectedInsight)}
                                    className="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium transition-colors shadow-sm flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>{t('addQuery')}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Business Value Banner */}
                    {selectedInsight?.value_description && (
                        <div className="bg-fuchsia-50/50 border border-fuchsia-200/70 rounded-xl p-4 mb-6">
                            <p className="text-sm">
                                <span className="font-bold text-[#8E24AA]">{t('businessValueLabel')}</span>
                                <span className="text-[#8E24AA]">{selectedInsight.value_description}</span>
                            </p>
                        </div>
                    )}

                    {/* No Queries State */}
                    {!hasQueries && !generating && (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Code className="w-8 h-8 text-primary-500" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('noQueriesTitle')}</h3>
                            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                                {t('noQueriesBody')}
                            </p>
                            <button
                                onClick={handleGenerateQueries}
                                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm flex items-center space-x-2.5 transition-colors inline-flex shadow-sm"
                            >
                                <Sparkles className="w-4 h-4" />
                                <span>{t('generateQueriesWithAI')}</span>
                            </button>
                        </div>
                    )}

                    {/* Generating State */}
                    {generating && (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Loader className="w-8 h-8 animate-spin text-primary-600" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('generatingQueriesTitle')}</h3>
                            <p className="text-sm text-gray-500 max-w-md mx-auto">
                                {t('generatingQueriesBody')}
                            </p>
                        </div>
                    )}

                    {/* Queries List */}
                    {hasQueries && selectedInsight && (
                        <div className="space-y-4">
                            {(selectedInsight.generated_queries || []).map((query) => (
                                <div key={query.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1 pr-6">
                                                <div className="flex items-center gap-2 mb-1.5">
                                                    <h4 className="font-bold text-gray-900 text-base">{query.name}</h4>
                                                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-md border ${
                                                        query.validation_status === 'validated'
                                                            ? 'bg-primary-50 text-primary-600 border-primary-200'
                                                            : query.validation_status === 'failed'
                                                            ? 'bg-red-50 text-red-600 border-red-200'
                                                            : 'bg-primary-50/50 text-primary-600 border-primary-200/50'
                                                    }`}>
                                                        {query.validation_status === 'validated' ? t('valid') : query.validation_status || t('valid')} {/* Defaulting to valid matching image styling */}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-500">
                                                    {query.description || t('defaultQueryDescription')}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleExecuteQuery(query)}
                                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-xs font-semibold flex items-center gap-2 transition-colors"
                                                >
                                                    <Play className="w-3.5 h-3.5" />
                                                    <span>{t('openQuery')}</span>
                                                </button>
                                                <button
                                                    onClick={() => handleCompareQuery(query)}
                                                    disabled={loadingCompareId === query.id}
                                                    className={`px-4 py-2 text-white rounded-lg text-xs font-semibold flex items-center gap-2 transition-colors ${
                                                        loadingCompareId === query.id
                                                            ? 'bg-[#8E24AA]/80 cursor-not-allowed'
                                                            : 'bg-[#8E24AA] hover:bg-[#7b1fa2]'
                                                    }`}
                                                >
                                                    {loadingCompareId === query.id ? (
                                                        <Loader className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <TrendingUp className="w-3.5 h-3.5" />
                                                    )}
                                                    <span>{t('compare')}</span>
                                                </button>
                                                <button
                                                    onClick={() => openDeleteModal(query)}
                                                    className="p-2 text-gray-400 hover:text-red-500 border border-gray-200 rounded-lg transition-colors bg-white hover:bg-red-50"
                                                    title={t('deleteQueryTooltip')}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Query Code Section */}
                                        <div className="relative bg-[#FAFAFA] rounded-xl border border-gray-100 p-4">
                                            <pre className="text-sm text-gray-600 overflow-x-auto whitespace-pre-wrap break-words font-mono pr-12">
                                                {query.query}
                                            </pre>
                                            <button
                                                onClick={() => copyToClipboard(query.query)}
                                                className="absolute top-4 right-4 p-1.5 text-gray-500 hover:text-gray-700 bg-white border border-gray-200 shadow-sm rounded-lg transition-colors"
                                                title={t('copyQueryTooltip')}
                                            >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirmModal.isOpen}
                onClose={closeDeleteModal}
                onConfirm={confirmDeleteQuery}
                title={deleteConfirmModal.title}
                message={deleteConfirmModal.message}
                confirmText={t('common:delete')}
                cancelText={t('common:cancel')}
            />

            {/* NLP Query Generation Modal */}
            {showNLPModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                        <MessageSquare className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">{t('generateQueryModalTitle')}</h3>
                                        <p className="text-xs text-gray-500">{t('forInsightLabel', { name: selectedInsightForNLP?.name })}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeNLPModal}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="mb-6">
                                {selectedInsightForNLP && (
                                    <div className="bg-primary-50 border border-primary-100 rounded-lg p-4 mb-4">
                                        <p className="text-sm font-medium text-primary-900 mb-1">{t('selectedInsightLabel')}</p>
                                        <p className="text-sm text-primary-800 font-semibold">{selectedInsightForNLP.name}</p>
                                        {selectedInsightForNLP.description && (
                                            <p className="text-xs text-primary-700 mt-1">{selectedInsightForNLP.description}</p>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('askQuestionLabel')}
                                    </label>
                                    <textarea
                                        value={nlpQuestion}
                                        onChange={handleNLPQuestionChange}
                                        placeholder={t('askQuestionPlaceholder')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none overflow-hidden custom-scrollbar"
                                        rows="4"
                                        style={{ minHeight: '100px', maxHeight: '300px' }}
                                        autoFocus
                                    />
                                    <p className="text-xs text-gray-500 mt-2">
                                        {t('aiQueryHint')}
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={closeNLPModal}
                                    disabled={generatingNLP}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {t('common:cancel')}
                                </button>
                                <button
                                    onClick={handleGenerateNLPQuery}
                                    disabled={generatingNLP || !nlpQuestion.trim()}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {generatingNLP ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            {t('generating')}
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4" />
                                            {t('generateQueryButton')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
